// © Microsoft Corporation. All rights reserved.

import { AppType } from "@msnews/experiences-constants";
import { PivotSwitchInfo } from "./Interfaces";
import { appEnvironment } from "@msnews/core";

/** Flag will be set to true if we parse a PeregrineRouterConfig where enableLocaleFeedPrefix is true */
export let enableLocaleFeedPrefix = false;

/**
 * Sets the enableLocaleFeedPrefix value
 * @param newEnableLocaleFeedPrefix new value for enableLocaleFeedPrefix
 */
export function updateEnableLocaleFeedPrefix(newEnableLocaleFeedPrefix: boolean): void {
    enableLocaleFeedPrefix = newEnableLocaleFeedPrefix;
}

/**
 * Formats route path
 * @param canonicalName canonical name for topic. If not an existing canonical name, then pass in the display string for the topic
 * @param id id for topic
 * @param usePrefix value to deactivate adding a prefix if unwanted. Default value is true and prefix is controlled by enableLocaleFeedPrefix
 * @param excludeFeedPrefix flag do disable including /feed in prefix
 */
export function getRoutePath(canonicalName: string, id?: string, usePrefix = true, excludeFeedPrefix = false): string {
    if (enableLocaleFeedPrefix && usePrefix) {
        let prefix = `/${appEnvironment.CurrentMarket}`;
        if (appEnvironment.AppType === AppType.Windows && !excludeFeedPrefix) {
            prefix += "/feed";
        }
        if (!canonicalName) {
            return prefix;
        }

        if (id) {
            return prefix + `/interest/${encodeURIComponent(canonicalName)}/${encodeURIComponent(id)}`;
        }

        const routeName = canonicalName[0] === "/" ? canonicalName.slice(1) : canonicalName;
        return prefix + (CanonicalNamePathMap[routeName] ? `${CanonicalNamePathMap[routeName]}` : `/${routeName}`);
    } else {
        if (!canonicalName) {
            return "";
        }

        if (id) {
            return `/interest/${encodeURIComponent(canonicalName)}/${encodeURIComponent(id)}`;
        }

        const routeName = canonicalName[0] === "/" ? canonicalName.slice(1) : canonicalName;
        return CanonicalNamePathMap[routeName] || `/${routeName}`;
    }
}

/**
 * Helper to check if the pivotSwitchInfo object is valid (has the pivot id)
 * @param pivotSwitchInfo pivot switch info object
 */
export function validatePivotSwitchInfo(pivotSwitchInfo: PivotSwitchInfo): boolean {
    if (!pivotSwitchInfo || !pivotSwitchInfo.oldSelection || !pivotSwitchInfo.newSelection) {
        return false;
    }

    if (!(pivotSwitchInfo.oldSelection && pivotSwitchInfo.oldSelection.pivotId)) {
        return false;
    }

    if (!(pivotSwitchInfo.newSelection && pivotSwitchInfo.newSelection.pivotId)) {
        return false;
    }

    return true;
}

/**
 * Get the relative routes path from external URL
 * @param externalUrl An anbsolute URL string
 */
export function getRoutePathFromExternalUrl(externalUrl: string) {
    try {
        return (new URL(externalUrl)).pathname;
    } catch {
        return null;
    }
}

/**
 * Consts for the route names
 */
export enum RouteNames {
    MyFeed = "myFeed",
    Personalize = "personalize",
    Saves = "saves"
}

/**
 * Map of canonical names to routes
 */
export const CanonicalNamePathMap: { [key: string]: string } = {
    myFeed: "/",
    personalize: "/personalize",
    interest: "/interest/*/:id",
    saves: "/personalize/saves"
};